import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  SearchOff as SearchIcon,
  FilterListOff as FilterIcon,
  Campaign as CampaignIcon,
  RocketLaunch as RocketIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const EmptyContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(6),
  textAlign: 'center',
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.spacing(2),
  border: `2px dashed ${theme.palette.grey[300]}`,
}));

const EmptyIcon = styled(Box)(({ theme }) => ({
  width: 120,
  height: 120,
  margin: '0 auto',
  marginBottom: theme.spacing(3),
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.light,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': {
    fontSize: 60,
    color: theme.palette.primary.main,
  },
}));

const CampaignEmptyState = ({ 
  hasSearchQuery = false, 
  hasFilters = false, 
  onCreateCampaign,
  onClearFilters 
}) => {
  
  if (hasSearchQuery || hasFilters) {
    return (
      <EmptyContainer elevation={0}>
        <EmptyIcon>
          <SearchIcon />
        </EmptyIcon>
        
        <Typography variant="h5" component="h2" gutterBottom color="text.primary">
          No campaigns found
        </Typography>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
          {hasSearchQuery && hasFilters 
            ? "No campaigns match your search query and filters. Try adjusting your search terms or clearing filters."
            : hasSearchQuery 
            ? "No campaigns match your search query. Try different keywords or check your spelling."
            : "No campaigns match your current filters. Try adjusting your filter criteria."
          }
        </Typography>
        
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={onClearFilters}
            size="large"
          >
            Clear Filters
          </Button>
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onCreateCampaign}
            size="large"
          >
            Create New Campaign
          </Button>
        </Stack>
      </EmptyContainer>
    );
  }

  // No campaigns at all
  return (
    <EmptyContainer elevation={0}>
      <EmptyIcon>
        <RocketIcon />
      </EmptyIcon>
      
      <Typography variant="h4" component="h2" gutterBottom color="text.primary">
        Ready to launch your first campaign?
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
        Create engaging social media campaigns from your website content. 
        Our AI will analyze your website and generate compelling posts across multiple platforms.
      </Typography>
      
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={onCreateCampaign}
        size="large"
        sx={{ mb: 4 }}
      >
        Create Your First Campaign
      </Button>
      
      <Box sx={{ mt: 6, pt: 4, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom color="text.primary">
          What you can do with campaigns:
        </Typography>
        
        <Stack 
          direction={{ xs: 'column', md: 'row' }} 
          spacing={4} 
          sx={{ mt: 3, textAlign: { xs: 'center', md: 'left' } }}
        >
          <Box sx={{ flex: 1 }}>
            <CampaignIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              AI-Powered Content
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Generate posts automatically from your website content and business goals
            </Typography>
          </Box>
          
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
              <Box sx={{ 
                width: 40, 
                height: 40, 
                borderRadius: '50%', 
                bgcolor: 'success.main', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold'
              }}>
                4
              </Box>
            </Box>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Multi-Platform
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create posts optimized for Twitter, Facebook, Instagram, and LinkedIn
            </Typography>
          </Box>
          
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
              <Box sx={{ 
                width: 40, 
                height: 40, 
                borderRadius: '50%', 
                bgcolor: 'warning.main', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold'
              }}>
                ⏰
              </Box>
            </Box>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Smart Scheduling
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Schedule posts at optimal times for maximum engagement
            </Typography>
          </Box>
        </Stack>
      </Box>
    </EmptyContainer>
  );
};

export default CampaignEmptyState; 